import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * The phone menu: at phone width (App.css "PHONE MENU", the same 640px) the
 * admin sidebar hides behind the top bar's menu button and slides in over the
 * page. Only the admin sidebar (`.app-sidebar`) does this.
 */
export const PHONE_QUERY = "(max-width: 640px)";

const matchesPhone = () => typeof window !== "undefined" && !!window.matchMedia?.(PHONE_QUERY).matches;

/**
 * [open, setOpen, isPhone]. The menu closes when a page is picked from it
 * (the page you're already on included), on any other route change, on
 * Escape, and when the screen grows past phone width (a rotation). Opening
 * moves keyboard focus into the menu; closing brings it back to the button.
 */
export function useMobileNav() {
  const [open, setOpen] = useState(false);
  const [isPhone, setIsPhone] = useState(matchesPhone);
  const { pathname } = useLocation();
  const wasOpen = useRef(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const mq = window.matchMedia?.(PHONE_QUERY);
    if (!mq) return undefined;
    const onChange = () => {
      setIsPhone(mq.matches);
      if (!mq.matches) setOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    // A page link closes it; Help (also a .nav-item) opens its own menu in the drawer.
    const onPick = (e) => { if (e.target.closest?.("#app-sidebar .nav-item:not(.help)")) setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("click", onPick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onPick);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      document.querySelector("#app-sidebar .nav-item")?.focus();
    } else if (wasOpen.current && document.activeElement?.closest?.("#app-sidebar")) {
      document.querySelector(".top-bar-menu-btn")?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  return [open, setOpen, isPhone];
}

/** The top bar's menu button (shown at phone width only). */
export function MobileNavButton({ open, onOpen }) {
  return (
    <button
      type="button"
      className="top-bar-menu-btn"
      onClick={onOpen}
      aria-label="Open menu"
      aria-expanded={open}
      aria-controls="app-sidebar"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  );
}

/** The dimmed page behind the open menu; a tap closes it. */
export function MobileNavBackdrop({ open, onClose }) {
  return open ? <div className="mobile-nav-backdrop" onClick={onClose} aria-hidden="true" /> : null;
}
